import { action, thunk } from 'easy-peasy'
import type { Action, Thunk } from 'easy-peasy'
import type { MetricsClient } from '@strata-monorepo/shared-types'
import { initializeNewRelic } from '@strata-monorepo/find-my-plan/src/lib/questionnaire/base/NewRelic/NewRelicSnippet'
import type { FMPStoreModel } from '../../types'
import { createAppropriateMetricsClient } from '../../hooks/useMetrics'
import { createMetricsClient } from '@strata-monorepo/metrics/lib/factory'

export interface MetricsModel {
  metricsClient: MetricsClient
  isMetricsProviderLoaded: boolean
  setIsMetricsProviderLoaded: Action<MetricsModel, boolean>
  setMetricsClient: Action<MetricsModel, MetricsClient>
  setMetricsClientToConsole: Action<MetricsModel>
  bootstrapMetricsClient: Thunk<MetricsModel, void, null, FMPStoreModel>
}

const metricsModel = (): MetricsModel => ({
  metricsClient: createMetricsClient('Console'),
  isMetricsProviderLoaded: false,

  // Actions
  setIsMetricsProviderLoaded: action((state, isMetricsProviderLoaded) => {
    state.isMetricsProviderLoaded = isMetricsProviderLoaded
  }),

  setMetricsClient: action((state, client) => {
    state.metricsClient = client
  }),

  setMetricsClientToConsole: action((state) => {
    state.metricsClient = createMetricsClient('Console')
  }),

  // Thunks
  bootstrapMetricsClient: thunk(async (actions, _, { getStoreState, getStoreActions }) => {
    const { setMetricsClient, setMetricsClientToConsole } = getStoreActions().metrics

    const storeState = getStoreState()

    const environment = storeState.env.environment

    if (environment !== 'production' && environment !== 'staging') {
      setMetricsClientToConsole()
      actions.setIsMetricsProviderLoaded(true)
      return
    }

    let isNewRelicLoaded: boolean
    try {
      isNewRelicLoaded = await initializeNewRelic()
    } catch (error) {
      console.error('Failed to initialize New Relic', error)
      isNewRelicLoaded = false
    }

    if (!isNewRelicLoaded) {
      setMetricsClientToConsole()
      actions.setIsMetricsProviderLoaded(true)
      return
    }

    try {
      const metricsClient = createAppropriateMetricsClient()
      setMetricsClient(metricsClient)
    } catch (error) {
      console.error('Failed to initialize New Relic logger', error)
      const metricsClient = createMetricsClient('Console')
      setMetricsClient(metricsClient)
    }

    // Set loaded flag AFTER metricsClient is set to avoid race condition
    actions.setIsMetricsProviderLoaded(true)
  }),
})

export default metricsModel
